import { BrowserRouter as Router } from 'react-router-dom';
import { Routes, Route, Navigate } from 'react-router-dom';
import JobListings from './pages/JobListings';
import JobApplications from './pages/JobApplications';
import Sidebar from './components/common/Sidebar';
import Agent from './pages/Agent';
import Login from './pages/Login';
import ProtectedRoute from './ProtectedRoute';
import { Toaster } from 'react-hot-toast';
import { useThemeStore } from './state/theme';
import ApplicationDetails from './pages/ApplicationDetails';
import Client from './pages/Client';
import Invoice from './pages/Invoice';
import MismatchRequests from './pages/MismatchRequests';
import LeaveRequests from './pages/LeaveRequests';
import ExportAllLogs from './pages/ExportAllLogs';
import { useAgentAuthStore, useAuthStore } from './state/auth';
import { useEffect } from 'react';
import PostJobFlow from './components/PostJobFlow';
import AgentInvoice from './pages/AgentInvoice';
import AgentView from './pages/AgentView';
import JobListing from './pages/JobListing';
import AgentProfile from './agent/AgentProfile';
import RCSE from './pages/Rcse';
import RcseView from './pages/RcseView';
import ClientInvoice from './pages/ClientInvoice';
import { getFirstAccessibleRoute } from './utils';
import ErrorBoundary from './components/ErrorBoundary';

const App: React.FC = () => {
  const { isDarkMode } = useThemeStore();
  const { isTokenExpired, permissions } = useAuthStore();
  const { agent } = useAgentAuthStore();

  useEffect(() => {
    isTokenExpired();
  }, []);

  // show only agent routes
  if (agent) {
    return (
      <ErrorBoundary>
        <Router>
          <div
            className={`flex min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}
          >
            <Routes>
              <Route path="/agent/profile" element={<AgentProfile />} />
              {/* Redirect any other routes to agent profile */}
              <Route
                path="*"
                element={<Navigate to="/agent/profile" replace />}
              />
            </Routes>
          </div>
          <Toaster position="top-right" />
        </Router>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <div
          className={`flex min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}
        >
          <Routes>
            {/* Agent Routes */}
            <Route path="/agent/profile" element={<AgentProfile />} />
            <Route
              path="/agent/*"
              element={<Navigate to="/agent/profile" replace />}
            />

            {/* Main Ops Routes */}
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <>
                  <Sidebar />
                  <main
                    className={`flex-1 p-4 ${isDarkMode ? 'bg-gray-900' : 'bg-greyTen'}`}
                  >
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <Navigate
                            to={getFirstAccessibleRoute(permissions)}
                            replace
                          />
                        }
                      />
                      <Route
                        path="/job-listings"
                        element={
                          <ProtectedRoute resource="jobListings">
                            <JobListings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/job-listings/create/:category"
                        element={
                          <ProtectedRoute resource="jobListings">
                            <PostJobFlow />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/job-listings/:id"
                        element={
                          <ProtectedRoute resource="jobListings">
                            <JobListing />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/jobs"
                        element={
                          <ProtectedRoute resource="jobApplications">
                            <JobApplications />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/agents"
                        element={
                          <ProtectedRoute resource="agents">
                            <Agent />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/agents/:id"
                        element={
                          <ProtectedRoute resource="agents">
                            <AgentView />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/agents/mismatch-requests"
                        element={
                          <ProtectedRoute resource="mismatch">
                            <MismatchRequests />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/agents/leave-requests"
                        element={
                          <ProtectedRoute resource="mismatch">
                            <LeaveRequests />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/agents/invoice"
                        element={
                          <ProtectedRoute resource="invoices">
                            <AgentInvoice />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/export-logs"
                        element={
                          <ProtectedRoute resource="logs">
                            <ExportAllLogs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients"
                        element={
                          <ProtectedRoute resource="clients">
                            <Client />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients/invoice"
                        element={
                          <ProtectedRoute resource="invoices">
                            <ClientInvoice />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients/rcse"
                        element={
                          <ProtectedRoute resource="requirement">
                            <RCSE />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients/rcse/:id"
                        element={
                          <ProtectedRoute resource="requirement">
                            <RcseView />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/agents/:id/invoice"
                        element={
                          <ProtectedRoute resource="invoices">
                            <Invoice />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/jobs/:id"
                        element={
                          <ProtectedRoute resource="jobApplications">
                            <ApplicationDetails />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </main>
                </>
              }
            />
          </Routes>
        </div>
        <Toaster position="top-right" />
      </Router>
    </ErrorBoundary>
  );
};

export default App;
